/* ==========================================================================
   UI behaviours

   Every interaction the original theme had, rewritten from its source: the
   announcement ticker, mobile menu, thermal reveal on the hero, the tech
   crossfade slider, FAQ and product accordions, scroll reveal, the sticky buy
   bar, the drop countdown, the product gallery, lazy reel videos and the
   free-shipping toast. Each one is self-contained and no-ops when its markup
   is absent, so the same file is safe to load on every page.
   ========================================================================== */

window.LUVZ = window.LUVZ || {};

LUVZ.ui = (function () {
  'use strict';

  function all(selector, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(selector));
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- announcement ticker ---------------------------------------
     Clone the marquee group until it covers the viewport twice, so the -50%
     keyframe always lands on a group boundary and the loop is seamless. The
     duration is derived from the width to hold a constant 60px/s at any size. */

  function ticker() {
    var bar = document.querySelector('[data-ticker]');
    if (!bar) return;
    var track = bar.querySelector('[data-ticker-track]');
    var group = track && track.firstElementChild;
    if (!group) return;

    function build() {
      while (track.children.length > 1) track.removeChild(track.lastElementChild);
      var width = group.offsetWidth;
      if (!width) return;
      var perHalf = Math.max(1, Math.ceil(bar.offsetWidth / width));
      for (var i = 1; i < perHalf * 2; i++) {
        var clone = group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }
      track.style.animationDuration = ((width * perHalf) / 60) + 's';
    }

    build();
    var timer;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(build, 150);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(build);
  }

  /* ---------- mobile menu ------------------------------------------------ */

  function mobileMenu() {
    var menu = document.getElementById('mmenu');
    if (!menu) return;

    function setExpanded(open) {
      all('[data-open-menu]').forEach(function (b) {
        b.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-open-menu]')) {
        setExpanded(menu.classList.toggle('show'));
        return;
      }
      if (e.target.closest('[data-close-menu]') || e.target.closest('#mmenu a')) {
        menu.classList.remove('show');
        setExpanded(false);
      }
    });

    /* the popup and the cart drawer sit above the menu, so they take Escape first */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !menu.classList.contains('show')) return;
      if (document.querySelector('[data-popup].show')) return;
      var drawer = document.getElementById('cart-drawer');
      if (drawer && drawer.classList.contains('show')) return;
      menu.classList.remove('show');
      setExpanded(false);
    });
  }

  /* ---------- lazy videos --------------------------------------------------
     Footage outside the reels rail (the worn clip next to the steps, the
     video slide in a product gallery) carries no src until it is on screen,
     plays while visible and pauses when it leaves. Reduced motion: load the
     poster only and let a tap play it. */

  function lazyVideos() {
    var vids = all('video.lazy-video');
    if (!vids.length) return;
    function load(v) { if (!v.src && v.dataset.src) v.src = v.dataset.src; }
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      vids.forEach(function (v) {
        v.addEventListener('click', function () { load(v); if (v.paused) v.play(); else v.pause(); });
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        if (en.isIntersecting) {
          load(v);
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: .35 });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* ---------- savings badge ----------------------------------------------
     "Save 11%" next to a struck compare-at price, worked out from the
     visitor's own market price list so it is never a made-up number. Hidden
     when the market has no compare-at price. */

  function saveBadges() {
    var badges = all('[data-save]');
    if (!badges.length || !LUVZ.localization) return;
    function paint() {
      badges.forEach(function (el) {
        var now = parseFloat(el.getAttribute('data-save-now'));
        var was = parseFloat(el.getAttribute('data-save-was'));
        if (!now || !was || was <= now) { el.hidden = true; return; }
        el.textContent = 'Save ' + Math.round((1 - now / was) * 100) + '%';
        el.hidden = false;
      });
    }
    paint();
    if (LUVZ.localization.onChange) LUVZ.localization.onChange(paint);
  }

  /* ---------- tech crossfade slider -------------------------------------- */

  function crossfade() {
    all('[data-crossfade]').forEach(function (box) {
      var slider = box.querySelector('input[type=range]');
      if (!slider) return;
      var cold = box.querySelector('.crossfade .cold');
      var out = box.querySelector('[data-slider-temp]');

      function update() {
        var value = +slider.value;
        var max = +slider.max || 40;
        if (cold) cold.style.opacity = (1 - (value / max)).toFixed(2);
        if (out) out.textContent = value + '°C';
      }
      slider.addEventListener('input', update);
      update();
    });
  }

  /* ---------- accordions -------------------------------------------------
     FAQ items are exclusive (opening one closes the rest); the product
     accordions toggle independently. Both animate via max-height. */

  function accordions() {
    document.addEventListener('click', function (e) {
      var faq = e.target.closest('.faq__q');
      if (faq) {
        var item = faq.parentElement;
        var wasOpen = item.classList.contains('open');
        all('.faq__item').forEach(function (i) {
          i.classList.remove('open');
          var a = i.querySelector('.faq__a');
          if (a) a.style.maxHeight = null;
          var q = i.querySelector('.faq__q');
          if (q) q.setAttribute('aria-expanded', 'false');
        });
        if (!wasOpen) {
          item.classList.add('open');
          faq.setAttribute('aria-expanded', 'true');
          var panel = item.querySelector('.faq__a');
          if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
        }
        return;
      }

      var acc = e.target.closest('.acc__q');
      if (acc) {
        var accItem = acc.parentElement;
        var panel2 = accItem.querySelector('.acc__a');
        var open = accItem.classList.toggle('open');
        acc.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel2) panel2.style.maxHeight = open ? panel2.scrollHeight + 'px' : null;
      }
    });
  }

  /* ---------- reveal on scroll ------------------------------------------- */

  function revealOnScroll() {
    var targets = all('.reveal');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('in');
        io.unobserve(en.target);
      });
    }, { threshold: 0.12 });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------- sticky buy bar --------------------------------------------
     Appears only once the main buy button has scrolled above the viewport, so
     a freshly loaded page never opens with the bar showing. */

  function buyBar() {
    var bar = document.getElementById('buybar');
    var anchor = document.getElementById('buybar-anchor');
    if (!bar || !anchor) return;

    /* and it steps aside once the footer arrives, rather than sitting on
       top of the links people scroll down there to find */
    var foot = document.querySelector('.footer');
    window.addEventListener('scroll', function () {
      var shown = anchor.getBoundingClientRect().bottom < 0 &&
        (!foot || foot.getBoundingClientRect().top > window.innerHeight - 40);
      bar.classList.toggle('show', shown);
      /* the newsletter chip yields to the buy bar — both sit bottom-centre */
      document.body.classList.toggle('buybar-visible', shown);
    }, { passive: true });
  }

  /* ---------- countdown --------------------------------------------------- */

  function countdown() {
    all('[data-countdown-ts]').forEach(function (el) {
      var end = parseInt(el.getAttribute('data-countdown-ts'), 10) * 1000;
      if (!end) return;

      function pad(n) { return String(n).padStart(2, '0'); }
      function set(sel, value) {
        var node = el.querySelector(sel);
        if (node) node.textContent = pad(value);
      }
      function tick() {
        var diff = end - Date.now();
        if (diff <= 0) { el.hidden = true; clearInterval(timer); return; }
        set('[data-d]', Math.floor(diff / 864e5));
        set('[data-h]', Math.floor(diff % 864e5 / 36e5));
        set('[data-m]', Math.floor(diff % 36e5 / 6e4));
        set('[data-s]', Math.floor(diff % 6e4 / 1e3));
      }
      var timer = setInterval(tick, 1000);
      tick();
    });
  }

  /* ---------- product gallery -------------------------------------------- */

  function gallery() {
    all('[data-gallery]').forEach(function (g) {
      var slider = g.querySelector('[data-slider]');
      if (!slider) return;
      var slides = all('.pdp__slide', slider);
      var dots = all('[data-dot]', g);
      var thumbs = all('[data-thumb]', g);
      if (slides.length < 2) return;

      function nearest() {
        var index = 0;
        var min = Infinity;
        slides.forEach(function (s, n) {
          var d = Math.abs(s.offsetLeft - slider.scrollLeft);
          if (d < min) { min = d; index = n; }
        });
        return index;
      }
      function goTo(i) {
        if (slides[i]) slider.scrollTo({ left: slides[i].offsetLeft, behavior: 'smooth' });
      }
      function mark(i) {
        dots.forEach(function (d, n) { d.classList.toggle('active', n === i); });
        thumbs.forEach(function (t, n) { t.classList.toggle('active', n === i); });
      }

      g.addEventListener('click', function (e) {
        var thumb = e.target.closest('[data-thumb]');
        if (thumb) { goTo(+thumb.getAttribute('data-index')); return; }
        var dot = e.target.closest('[data-dot]');
        if (dot) { goTo(dots.indexOf(dot)); return; }
        if (e.target.closest('[data-slide-prev]')) goTo(Math.max(0, nearest() - 1));
        if (e.target.closest('[data-slide-next]')) goTo(Math.min(slides.length - 1, nearest() + 1));
      });

      var timer;
      slider.addEventListener('scroll', function () {
        clearTimeout(timer);
        timer = setTimeout(function () { mark(nearest()); }, 90);
      });
    });
  }

  /* ---------- reels -------------------------------------------------------
     Videos carry no src until they scroll into view, so a page visit costs
     nothing until a reel is actually on screen. Under reduced motion they
     load but do not autoplay — tapping a card plays it. */

  function reels() {
    var root = document.querySelector('[data-reels]');
    if (!root) return;
    var videos = all('.reel__video', root);
    if (!videos.length) return;

    function load(v) {
      if (!v.src && v.dataset.src) v.src = v.dataset.src;
    }
    function play(v) {
      load(v);
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked */ });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting && en.intersectionRatio >= 0.5) {
            if (reducedMotion.matches) load(v); else play(v);
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: [0, 0.5] });
      videos.forEach(function (v) { io.observe(v); });
    } else {
      videos.forEach(function (v) { reducedMotion.matches ? load(v) : play(v); });
    }

    root.addEventListener('click', function (e) {
      if (!reducedMotion.matches) return;
      var reel = e.target.closest('[data-reel]');
      if (!reel || e.target.closest('a')) return;
      var v = reel.querySelector('.reel__video');
      if (!v) return;
      if (v.paused) play(v); else v.pause();
    });
  }

  /* ---------- nav scroll state ------------------------------------------- */

  function trustAutoScroll() {
    var row = document.querySelector('.hero .trust__row');
    if (!row || reducedMotion.matches) return;

    var narrow = window.matchMedia('(max-width: 680px)');
    var paused = false;
    var resumeTimer = null;

    row.classList.add('is-autoplay');

    var pos = row.scrollLeft;
    var last = null;
    var pxPerSec = 40;

    function frame(now) {
      if (last === null) last = now;
      var dt = now - last;
      last = now;
      if (!paused && narrow.matches) {
        pos += pxPerSec * (dt / 1000);
        var max = row.scrollWidth - row.clientWidth;
        if (pos >= max) pos = 0;
        row.scrollLeft = pos;
      } else {
        pos = row.scrollLeft;
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    function pause() {
      paused = true;
      row.classList.remove('is-autoplay');
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        paused = false;
        row.classList.add('is-autoplay');
      }, 3200);
    }

    row.addEventListener('touchstart', pause, { passive: true });
    row.addEventListener('pointerdown', pause);
    row.addEventListener('wheel', pause, { passive: true });
  }

  function navScroll() {
    var nav = document.querySelector('.nav');
    if (!nav) return;
    function onScroll() {
      nav.classList.toggle('is-scrolled', window.scrollY > 8);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- search overlay ----------------------------------------------
     Client-side search over LUVZ.products — the catalogue is small enough
     that filtering titles/subtitles in memory is instant and offline-safe. */

  function searchOverlay() {
    var root = document.querySelector('[data-search]');
    if (!root) return;
    var input = root.querySelector('[data-search-input]');
    var results = root.querySelector('[data-search-results]');
    var opener = null;
    var active = -1;

    function items() {
      var P = LUVZ.products || {};
      return Object.keys(P).map(function (h) {
        var p = P[h];
        return { handle: h, title: p.title, sub: root.getAttribute('data-result-sub') || p.subtitle, group: p.group, img: p.thumb || p.image };
      });
    }

    function urlFor(handle) {
      var p = LUVZ.products[handle];
      return (p && p.url) || ('/products/' + handle);
    }

    function priceFor(handle) {
      var p = LUVZ.products[handle];
      return p ? p.priceText : '';
    }

    function render(q) {
      var query = (q || '').trim().toLowerCase();
      var list = items().filter(function (p) {
        if (!query) return true;
        return (p.title + ' ' + p.sub + ' ' + p.group).toLowerCase().indexOf(query) > -1;
      });
      active = -1;
      var page = root.getAttribute('data-search-page');
      var more = page ? '<a class="search__all" href="' + page + (query ? '?q=' + encodeURIComponent(query) : '') + '">See all results →</a>' : '';
      if (!list.length) {
        results.innerHTML = '<p class="search__empty">Nothing found — try “frost”, “camo” or “thermos”.</p>' + more;
        return;
      }
      results.innerHTML = '<p class="search__count">' + list.length + ' product' + (list.length === 1 ? '' : 's') + '</p>' +
        list.map(function (p, i) {
          return '<a class="sres" href="' + urlFor(p.handle) + '" data-idx="' + i + '">' +
            '<img class="sres__img" src="' + p.img + '" alt="" loading="lazy" width="60" height="60">' +
            '<span><span class="sres__t">' + p.title + '</span>' +
            '<span class="sres__sub">' + p.sub + ' · ' + p.group + '</span></span>' +
            '<span class="sres__price">' + priceFor(p.handle) + '</span>' +
            '<span class="sres__go">View →</span>' +
          '</a>';
        }).join('') + more;
    }

    function open() {
      opener = document.activeElement;
      root.classList.add('open');
      root.setAttribute('aria-hidden', 'false');
      render(input ? input.value : '');
      setTimeout(function () { if (input) input.focus(); }, 60);
    }

    function close() {
      root.classList.remove('open');
      root.setAttribute('aria-hidden', 'true');
      if (opener && opener.focus) { opener.focus(); opener = null; }
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-open-search]')) { e.preventDefault(); open(); }
      if (e.target.closest('[data-close-search]')) { e.preventDefault(); close(); }
    });
    root.addEventListener('click', function (e) {
      if (e.target === root) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('open')) close();
      /* "/" focuses search from anywhere, like most storefronts */
      if (e.key === '/' && !root.classList.contains('open') &&
          !/^(input|textarea|select)$/i.test(document.activeElement.tagName)) {
        e.preventDefault();
        open();
      }
      if (root.classList.contains('open') && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        var links = all('.sres', results);
        if (!links.length) return;
        e.preventDefault();
        active += (e.key === 'ArrowDown' ? 1 : -1);
        active = Math.max(0, Math.min(links.length - 1, active));
        links.forEach(function (l, i) { l.classList.toggle('is-active', i === active); });
        links[active].scrollIntoView({ block: 'nearest' });
      }
      if (root.classList.contains('open') && e.key === 'Enter' && document.activeElement === input) {
        var links = all('.sres', results);
        var target = links[active] || links[0];
        var page = root.getAttribute('data-search-page');
        if (target) window.location.href = target.href;
        else if (page && input.value.trim()) window.location.href = page + '?q=' + encodeURIComponent(input.value.trim());
      }
    });
    if (input) input.addEventListener('input', function () { render(input.value); });
    if (LUVZ.localization && LUVZ.localization.onChange) {
      LUVZ.localization.onChange(function () { if (root.classList.contains('open')) render(input ? input.value : ''); });
    }
  }

  /* ---------- reaction demo tabs -------------------------------------------
     The "see the shift" demo: each tab swaps which product sits in the
     thermal viewer. Panels are plain markup; this just toggles visibility. */

  function demoTabs() {
    all('[data-demo]').forEach(function (demo) {
      var tabs = all('[data-demo-tab]', demo);
      var panes = all('[data-demo-pane]', demo);
      if (!tabs.length || !panes.length) return;
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var key = tab.getAttribute('data-demo-tab');
          tabs.forEach(function (t) {
            var on = t === tab;
            t.classList.toggle('is-active', on);
            t.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          panes.forEach(function (p) {
            p.hidden = p.getAttribute('data-demo-pane') !== key;
          });
        });
      });
    });
  }

  /* ---------- review tools --------------------------------------------------
     Rating filter chips, sort chips and progressive reveal for long review
     lists. Ratings are parsed from each card's star aria-label, dates from
     the <time datetime> — no markup changes needed on the cards themselves. */

  function escapeReview(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function reviewCard(review) {
    var stars = Array.from({ length: 5 }, function (_, index) {
      return '<span aria-hidden="true">' + (index < review.rating ? '★' : '☆') + '</span>';
    }).join('');
    return '<li class="review-card">' +
      '<div class="review-card__head"><span class="review-card__author">' + escapeReview(review.name) + '</span>' +
      '<time class="review-card__date" datetime="' + escapeReview(review.date) + '">' + escapeReview(review.date) + '</time></div>' +
      '<div class="rating" aria-label="' + review.rating + ' out of 5 stars">' + stars + '</div>' +
      (review.title ? '<p class="review-card__title">' + escapeReview(review.title) + '</p>' : '') +
      '<p class="review-card__body">' + escapeReview(review.body) + '</p>' +
      '<span class="review-card__source">' + (review.verified ? '✓ Verified buyer' : 'Customer review') +
      (review.from ? ' · ' + escapeReview(review.from) : '') + '</span></li>';
  }

  function reviewTools() {
    all('[data-reviews]').forEach(function (block) {
      if (block.getAttribute('data-reviews-managed') === 'true') return;
      var list = block.querySelector('.reviews__list');
      if (!list) return;
      var handle = block.getAttribute('data-reviews');
      var data = window.LUVZ && LUVZ.reviewData && LUVZ.reviewData[handle];
      if (data && Array.isArray(data.reviews)) {
        list.innerHTML = data.reviews.map(reviewCard).join('');
        var average = block.querySelector('.reviews__average');
        var count = block.querySelector('.reviews__count');
        if (average && data.average != null) average.textContent = Number(data.average).toFixed(2);
        if (count) count.textContent = data.total + (data.total === 1 ? ' review' : ' reviews');
        all('.reviews__bar', block).forEach(function (bar, index) {
          var rating = String(5 - index);
          var value = Number(data.distribution[rating] || 0);
          var fill = bar.querySelector('.reviews__bar-fill');
          var number = bar.querySelector('.reviews__bar-count');
          if (fill) fill.style.width = (data.total ? (value / data.total) * 100 : 0).toFixed(1) + '%';
          if (number) number.textContent = value;
        });
      }
      var cards = all('.review-card', list);
      if (cards.length < 4) return;

      var LIMIT = parseInt(block.getAttribute('data-rev-limit') || '8', 10);
      var pageSize = window.matchMedia('(max-width: 680px)').matches ? Math.min(4, LIMIT) : LIMIT;
      var filter = 'all';
      var sort = 'new';
      var visibleCount = pageSize;

      cards.forEach(function (card) {
        var time = card.querySelector('time[datetime]');
        if (!time) return;
        var parsed = new Date(time.getAttribute('datetime') + 'T00:00:00');
        if (!isNaN(parsed.getTime())) {
          time.textContent = new Intl.DateTimeFormat('en', {
            month: 'short', day: 'numeric', year: 'numeric'
          }).format(parsed);
        }
      });

      function ratingOf(card) {
        var r = card.querySelector('.rating[aria-label]');
        var m = r && r.getAttribute('aria-label').match(/([\d.]+)/);
        return m ? parseFloat(m[1]) : 0;
      }
      function dateOf(card) {
        var t = card.querySelector('time[datetime]');
        var d = t ? Date.parse(t.getAttribute('datetime')) : 0;
        return isNaN(d) ? 0 : d;
      }

      function apply() {
        var shown = 0;
        var sorted = cards.slice().sort(function (a, b) {
          if (sort === 'high') return ratingOf(b) - ratingOf(a);
          if (sort === 'low') return ratingOf(a) - ratingOf(b);
          return dateOf(b) - dateOf(a);
        });
        sorted.forEach(function (c) { list.appendChild(c); });
        sorted.forEach(function (c) {
          var ok = filter === 'all' ||
            (filter === '5' && ratingOf(c) === 5) ||
            (filter === '4' && ratingOf(c) === 4) ||
            (filter === 'low' && ratingOf(c) <= 3);
          var vis = ok && shown < visibleCount;
          c.hidden = !vis;
          if (vis) shown++;
        });
        var moreBtn = block.querySelector('[data-rev-more]');
        var totalMatch = sorted.filter(function (c) {
          return filter === 'all' ||
            (filter === '5' && ratingOf(c) === 5) ||
            (filter === '4' && ratingOf(c) === 4) ||
            (filter === 'low' && ratingOf(c) <= 3);
        }).length;
        if (moreBtn) {
          moreBtn.hidden = totalMatch <= visibleCount;
          var n = moreBtn.querySelector('[data-rev-more-n]');
          if (n) n.textContent = Math.min(pageSize, Math.max(0, totalMatch - visibleCount));
        }
        var empty = block.querySelector('[data-rev-empty]');
        if (empty) empty.hidden = totalMatch > 0;
      }

      all('[data-rev-filter]', block).forEach(function (chip) {
        chip.addEventListener('click', function () {
          filter = chip.getAttribute('data-rev-filter');
          all('[data-rev-filter]', block).forEach(function (c) {
            var on = c === chip;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          visibleCount = pageSize;
          apply();
        });
      });

      all('[data-rev-sort]', block).forEach(function (chip) {
        chip.addEventListener('click', function () {
          sort = chip.getAttribute('data-rev-sort');
          all('[data-rev-sort]', block).forEach(function (c) {
            var on = c === chip;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          apply();
        });
      });

      var moreBtn = block.querySelector('[data-rev-more]');
      if (moreBtn) moreBtn.addEventListener('click', function () {
        visibleCount += pageSize;
        apply();
      });

      apply();
    });
  }

  /* ---------- checkout terms gate ------------------------------------------
     The drawer asks for policy agreement before handing off to checkout. */

  function checkoutGate() {
    /* every checkout button (drawer and cart page) is gated on the terms box
       that sits next to it, which is also where the no-returns policy is
       accepted */
    all('[data-checkout]').forEach(function (link) {
      var scope = link.closest('.drawer__foot, .cartpage__sum') || document;
      var box = scope.querySelector('[data-terms]');
      var err = scope.querySelector('[data-terms-err]');
      if (!box) return;
      link.addEventListener('click', function (e) {
        if (box.checked) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (err) err.hidden = false;
        box.focus();
      });
      box.addEventListener('change', function () {
        if (box.checked && err) err.hidden = true;
      });
    });
  }

  /* ---------- static preview guards --------------------------------------
     Checkout, customer accounts and the newsletter/contact endpoints are
     served by Shopify. On the static preview those URLs do not exist, so
     instead of a 404 the visitor gets a clear note. On Shopify
     (window.Shopify is defined) this does nothing. */

  function previewGuards() {
    if (window.Shopify) return;
    var note;
    function say(msg) {
      if (!note) {
        note = document.createElement('div');
        note.className = 'preview-note';
        note.setAttribute('role', 'status');
        document.body.appendChild(note);
      }
      note.textContent = msg;
      note.classList.add('is-on');
      clearTimeout(note._t);
      note._t = setTimeout(function () { note.classList.remove('is-on'); }, 4200);
    }
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href="/checkout"], [data-shopify-link]');
      if (!a) return;
      e.preventDefault();
      say(a.hasAttribute('data-checkout')
        ? 'Preview build — checkout opens here once the theme is live on Shopify.'
        : 'Preview build — accounts go live with the Shopify store.');
    });
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f.matches('[data-product-form], [data-newsletter-form], .searchpage__form')) return;
      var action = f.getAttribute('action') || '';
      if (action.charAt(0) !== '/') return;
      e.preventDefault();
      say('Preview build — this form sends once the theme is live on Shopify.');
    });
  }

  /* ---------- copy to clipboard ------------------------------------------- */

  function copyButtons() {
    all('[data-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var text = btn.getAttribute('data-copy');
        var label = btn.querySelector('[data-copy-label]');
        function done() {
          if (!label) return;
          label.textContent = 'Copied ✓';
          setTimeout(function () { label.textContent = 'Copy'; }, 1600);
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, function () {});
        }
      });
    });
  }

  /* ---------- search page ---------------------------------------------------
     pages/search.html carries every product card; this filters them by ?q=
     so the page works as a real results page and stays crawlable. */

  function searchPage() {
    var root = document.querySelector('[data-search-page-root]');
    if (!root) return;
    var input = root.querySelector('[data-searchpage-input]');
    var count = root.querySelector('[data-searchpage-count]');
    var none = root.querySelector('[data-searchpage-none]');
    var cards = all('[data-search-item]', root);
    var q = (new URLSearchParams(location.search).get('q') || '').trim();
    if (input) input.value = q;
    function run(query) {
      var words = query.toLowerCase().split(/\s+/).filter(Boolean);
      var n = 0;
      cards.forEach(function (c) {
        var hay = c.getAttribute('data-search-item');
        var hit = words.every(function (w) { return hay.indexOf(w) > -1; });
        c.hidden = !hit;
        if (hit) n += 1;
      });
      if (count) count.textContent = query ? n + ' result' + (n === 1 ? '' : 's') + ' for “' + query + '”' : 'All ' + cards.length + ' products';
      if (none) none.hidden = n > 0;
    }
    run(q);
    if (input) input.addEventListener('input', function () { run(input.value.trim()); });
  }

  /* ---------- free-shipping toast ---------------------------------------- */

  function shippingToast() {
    var toast = document.getElementById('fs-toast');
    if (!toast) return;
    var KEY = 'luvz_fs_toast_seen';
    try { if (sessionStorage.getItem(KEY)) return; } catch (e) { /* no-op */ }

    var done = false;
    function hide() {
      if (done) return;
      done = true;
      toast.classList.remove('is-on');
      setTimeout(function () { toast.hidden = true; }, 400);
      try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* no-op */ }
    }

    var dismiss = toast.querySelector('.fs-toast__x');
    if (dismiss) dismiss.addEventListener('click', hide);
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-open-cart],[data-add-to-cart]')) hide();
    });

    setTimeout(function () {
      if (done) return;
      toast.hidden = false;
      requestAnimationFrame(function () { toast.classList.add('is-on'); });
      setTimeout(hide, 8000);
    }, 1500);
  }

  function init() {
    document.documentElement.classList.remove('no-js');
    ticker();
    mobileMenu();
    navScroll();
    searchOverlay();
    lazyVideos();
    saveBadges();
    demoTabs();
    crossfade();
    accordions();
    revealOnScroll();
    buyBar();
    countdown();
    gallery();
    trustAutoScroll();
    reviewTools();
    checkoutGate();
    previewGuards();
    copyButtons();
    searchPage();
    reels();
    shippingToast();
  }

  return { init: init };
}());
